import { ParagraphSnapshot } from '../../../core/types/Typesetter';

/**
 * LayoutResponseHandler - Utility for handling layout response messages from worker
 *
 * Processes layout responses from the Typesetter Web Worker, including stale response
 * detection, error handling, result caching, and promise resolution/rejection.
 * Ensures proper handling of concurrent requests and maintains request state consistency.
 */
export class LayoutResponseHandler {
  /**
   * Handle layout response from worker
   *
   * This method processes layout responses by:
   * 1. Finding the corresponding pending request
   * 2. Checking if the response is from the latest request (stale response detection)
   * 3. Handling errors by rejecting the promise
   * 4. Caching successful results and resolving the promise
   *
   * @param payload - Raw response payload from worker message
   * @param pendingRequests - Map of pending request IDs to promise handlers
   * @param latestRequestId - The ID of the most recent request
   * @param lastLayoutResult - Reference to store the last successful layout result
   */
  static handleLayoutResponse(
    payload: any,
    pendingRequests: Map<string, {
      resolve: (result: ParagraphSnapshot) => void;
      reject: (error: Error) => void;
      requestId: number;
    }>,
    latestRequestId: number,
    lastLayoutResult: { current: ParagraphSnapshot | null }
  ): void {
    const { id, result, error } = payload;

    const pending = pendingRequests.get(id);
    if (!pending) {
      console.warn('[LayoutResponseHandler] No pending request for:', id);
      return;
    }

    // 🆕 Check if this response is from the latest request
    const isLatest = pending.requestId === latestRequestId;

    if (!isLatest) {
      console.log(`⏩ [LayoutResponseHandler] Ignoring stale response #${pending.requestId} (latest is #${latestRequestId})`);
      pendingRequests.delete(id);
      // Don't reject - just silently ignore stale responses
      return;
    }

    pendingRequests.delete(id);

    if (error) {
      pending.reject(new Error(error));
    } else {
      // 🆕 Cache the layout result before resolving (includes plan_hash)
      lastLayoutResult.current = result;
      console.log(`💾 [LayoutResponseHandler] Cached layout result #${pending.requestId} with hash:`, result?.plan_hash);

      pending.resolve(result);
    }
  }
}
